import { useState, useCallback } from 'react';

import { NotificationSeverity } from '@/components/notifications/Notification';
import useNotification from '@/hooks/useNotification';
import {
  ExportJobStatus,
  InitiateExportResponse,
} from '@/pages/Business/pages/Monitoring/models/Export';
import { ShippingsFilter } from '../models';
import {
  useCountShippingExportMutation,
  useInitiateShippingExportMutation,
  useLazyGetShippingExportJobStatusQuery,
} from '../services/shippings.service';
import {
  buildShippingExportRequestBody,
  getShippingExportDateRangeValidationMessage,
} from '../utils/buildShippingExportRequestBody';
import { getErrorMessage } from '@/utils/errorUtils';

const POLLING_INTERVAL_MS = 3000;
const MAX_POLLING_DURATION_MS = 15 * 60 * 1000;

export const useShippingsExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [countExport] = useCountShippingExportMutation();
  const [initiateExport] = useInitiateShippingExportMutation();
  const [getJobStatus] = useLazyGetShippingExportJobStatusQuery();
  const { showNotification } = useNotification();

  const pollJobStatus = useCallback(
    async (jobId: string, startedAtMs: number) => {
      try {
        if (Date.now() - startedAtMs > MAX_POLLING_DURATION_MS) {
          throw new Error(
            'El reporte tardó demasiado en generarse. Verifique que Hermes esté procesando el evento shipping_export_excel.'
          );
        }

        const { data: job, error } = await getJobStatus(jobId);

        if (error || !job) {
          throw new Error('Error al consultar el estado del reporte');
        }

        if (job.status === ExportJobStatus.COMPLETED && job.downloadUrl) {
          const link = document.createElement('a');
          link.href = job.downloadUrl;
          link.setAttribute('download', job.fileName || 'reporte-envios.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          showNotification(
            'Reporte generado con éxito',
            NotificationSeverity.success
          );
          setIsExporting(false);
          return;
        }

        if (job.status === ExportJobStatus.ERRORED) {
          throw new Error(
            job.errorMessage || 'Error en el servidor al generar el reporte'
          );
        }

        setTimeout(
          () => pollJobStatus(jobId, startedAtMs),
          POLLING_INTERVAL_MS
        );
      } catch (err: unknown) {
        const message = getErrorMessage(err, 'Error al generar el reporte');
        showNotification(message, NotificationSeverity.error);
        setIsExporting(false);
      }
    },
    [getJobStatus, showNotification]
  );

  const handleExport = useCallback(
    async (filters: ShippingsFilter) => {
      if (isExporting) return;

      const validationMessage = getShippingExportDateRangeValidationMessage(filters);
      if (validationMessage) {
        showNotification(validationMessage, NotificationSeverity.error);
        return;
      }

      setIsExporting(true);
      try {
        const body = buildShippingExportRequestBody(filters);

        const { count, limit, emptyReason, countIgnoringDates } =
          await countExport(body).unwrap();

        if (count === 0) {
          showNotification(
            emptyReason === 'OUT_OF_DATE_RANGE'
              ? `No hay envíos en el rango de fechas elegido. Con el resto de los filtros hay ${(countIgnoringDates ?? 0).toLocaleString()} envíos fuera de ese rango: ampliá o corregí las fechas para incluirlos.`
              : 'No hay envíos con los filtros seleccionados.',
            NotificationSeverity.error
          );
          setIsExporting(false);
          return;
        }

        if (count > limit) {
          showNotification(
            `Hay ${count.toLocaleString()} envíos con esos filtros, el máximo exportable es ${limit.toLocaleString()}. Reducí el rango de fechas u otros filtros para exportar.`,
            NotificationSeverity.error
          );
          setIsExporting(false);
          return;
        }

        const response: InitiateExportResponse = await initiateExport(body).unwrap();

        showNotification(
          `Generando reporte con ${count.toLocaleString()} envíos, por favor espere...`,
          NotificationSeverity.info
        );

        pollJobStatus(response.jobId, Date.now());
      } catch (err: unknown) {
        const message = getErrorMessage(
          err,
          'No se pudo iniciar la exportación'
        );
        showNotification(message, NotificationSeverity.error);
        setIsExporting(false);
      }
    },
    [countExport, initiateExport, isExporting, pollJobStatus, showNotification]
  );

  return {
    handleExport,
    isExporting,
    getExportDateRangeValidationMessage: getShippingExportDateRangeValidationMessage,
  };
};
